import { type ReactNode, createContext, useCallback, useContext, useMemo, useState } from 'react';

import { CallQualityFeedbackModal } from './call-quality-feedback-modal';

type OpenCallQualityFeedback = (ids?: {
  callId?: string | null;
  deviceLegId?: string | null;
}) => void;

const CallQualityFeedbackContext = createContext<OpenCallQualityFeedback | null>(null);

export const CallQualityFeedbackProvider = ({ children }: { children: ReactNode }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeCallId, setActiveCallId] = useState<string | null>(null);
  const [activeDeviceLegId, setActiveDeviceLegId] = useState<string | null>(null);

  const open = useCallback<OpenCallQualityFeedback>((ids) => {
    setActiveCallId(ids?.callId ?? null);
    setActiveDeviceLegId(ids?.deviceLegId ?? null);
    setIsOpen(true);
  }, []);

  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  // Re-create context value only when `open` identity changes (it doesn't).
  const value = useMemo(() => open, [open]);

  return (
    <CallQualityFeedbackContext.Provider value={value}>
      {children}
      <CallQualityFeedbackModal
        isOpen={isOpen}
        onClose={handleClose}
        callId={activeCallId}
        deviceLegId={activeDeviceLegId}
      />
    </CallQualityFeedbackContext.Provider>
  );
};

export const useOpenCallQualityFeedback = (): OpenCallQualityFeedback => {
  const ctx = useContext(CallQualityFeedbackContext);
  if (!ctx) {
    throw new Error('useOpenCallQualityFeedback must be used within <CallQualityFeedbackProvider>');
  }
  return ctx;
};
